/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useEffect, useState } from 'react';
import { ClientsService } from '@services/requests/housien-services/ClientsService';
import { PropertiesService } from '@services/requests/housien-services/PropertiesService';
import { propertyHeadline } from '@/shared/utils/property-display';
import { useAgency } from '@pages/authenticated/agency-pages/AgenciesContext';
import type { Deal } from '@/services/requests/housien-services/DealsService';

export interface DealPartyNames {
  clientName: string | null;
  propertyName: string | null;
  brokerName: string | null;
  /** True while the client name is still being fetched. */
  clientResolving: boolean;
  /** True while the property name is still being fetched. */
  propertyResolving: boolean;
}

/**
 * Display name of a client record, across the flat and nested shapes.
 */
function clientDisplayName(client: Record<string, any>): string | null {
  return (
    client.personal_info?.full_name
    ?? client.full_name
    ?? client.identity?.corporate_name
    ?? client.corporate_name
    ?? null
  );
}

/** True when the name is already known or cannot be fetched. */
function skipNameFetch(
  agencyId: string | undefined,
  id: string | null | undefined,
  known: string | null | undefined,
): boolean {
  return !agencyId || !id || Boolean(known);
}

/** A name that may still be in flight. */
interface ResolvedName {
  name: string | null;
  resolving: boolean;
}

/**
 * Picks what to show for a party: the seed the deal carries, the name a
 * finished fetch settled on, or nothing while one is still in flight.
 *
 * Split out of `useResolvedName` so the hook keeps only its effect: the
 * choice is pure, and the cognitive-complexity gate reads both halves
 * well under the limit.
 *
 * @param known - The name the deal itself carries, if any.
 * @param skip - Whether this party is not fetchable (no agency or id).
 * @param id - The party's id on the deal.
 * @param settled - The result of the last completed fetch, if any.
 * @returns The resolved name and whether a fetch is still in flight.
 */
function pickResolved(
  known: string | null | undefined,
  skip: boolean,
  id: string | null | undefined,
  settled: { id: string; name: string | null } | null,
): ResolvedName {
  if (known) return { name: known, resolving: false };
  if (skip) return { name: null, resolving: false };
  if (settled !== null && settled.id === id) {
    return { name: settled.name, resolving: false };
  }
  return { name: null, resolving: true };
}

/**
 * Resolves one party's display name, best-effort.
 *
 * Seeds from the name the deal already carries and fetches the record
 * only when that seed is missing. `resolving` is derived from the id
 * rather than stored, so it is already true on the first render — the
 * caller never has a frame in which the name is absent and no fetch is
 * yet flagged, which is what used to flash the raw id on screen.
 *
 * @param agencyId - The owning agency.
 * @param id - The party's id on the deal.
 * @param known - The name the deal itself carries, if any.
 * @param fetchName - Fetches the display name for the id.
 * @returns The resolved name and whether a fetch is still in flight.
 */
function useResolvedName(
  agencyId: string | undefined,
  id: string | null | undefined,
  known: string | null | undefined,
  fetchName: (agencyId: string, id: string) => Promise<string | null>,
): ResolvedName {
  const [settled, setSettled] = useState<{ id: string; name: string | null } | null>(null);

  useEffect(() => {
    if (skipNameFetch(agencyId, id, known)) return;
    let cancelled = false;
    const done = (name: string | null) => { if (!cancelled) setSettled({ id: id!, name }); };
    fetchName(agencyId!, id!).then(done).catch(() => done(null));
    return () => { cancelled = true; };
    // `fetchName` is a module-level fetcher; only the ids drive a refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agencyId, id, known]);

  return pickResolved(known, skipNameFetch(agencyId, id, known), id, settled);
}

/** Fetches a client's display name for the deal header. */
function fetchClientName(agencyId: string, clientId: string): Promise<string | null> {
  return ClientsService.getAgencyClient(agencyId, clientId)
    .then((client) => clientDisplayName(client as Record<string, any>));
}

/** Fetches a property's headline for the deal header. */
function fetchPropertyName(agencyId: string, propertyId: string): Promise<string | null> {
  return PropertiesService.getProperty(agencyId, propertyId)
    .then((property) => propertyHeadline(property as unknown as Record<string, unknown>) ?? null);
}

/** The subset of an agency member this hook needs to name a broker. */
interface BrokerMember {
  email?: string;
  first_name?: string;
  last_name?: string;
}

/**
 * The broker's full name, drawn from the agency roster, or null.
 *
 * @param members - The agency members, or undefined while loading.
 * @param brokerId - The deal's broker email.
 * @returns The trimmed full name, or null when no member matches.
 */
function brokerDisplayName(
  members: BrokerMember[] | undefined,
  brokerId: string | undefined,
): string | null {
  const broker = members?.find((m) => m.email === brokerId);
  return broker ? `${broker.first_name} ${broker.last_name}`.trim() : null;
}

/**
 * Resolves the display names behind a deal's client, property, and broker
 * ids, best-effort: `GET /deals/{id}` returns bare ids, so each name is
 * fetched separately; a name still in flight is flagged as resolving so
 * the caller can hold a placeholder instead of exposing the raw id.
 *
 * @param agencyId - The owning agency.
 * @param deal - The loaded deal, or null while loading.
 * @returns The resolved names; each null until (and unless) it resolves.
 */
export function useDealPartyNames(agencyId: string | undefined, deal: Deal | null): DealPartyNames {
  const client = useResolvedName(agencyId, deal?.client_id, deal?.client_name, fetchClientName);
  const property = useResolvedName(agencyId, deal?.property_id, deal?.property_title, fetchPropertyName);
  const members = useAgency((state) => state.agency?.members);
  const brokerName = brokerDisplayName(members, deal?.broker_id);

  return {
    clientName: client.name,
    propertyName: property.name,
    brokerName,
    clientResolving: client.resolving,
    propertyResolving: property.resolving,
  };
}
